import ClearableInput from '@rio-cloud/rio-uikit/components/clearableInput/ClearableInput';
import Icon from '@rio-cloud/rio-uikit/components/icon/Icon';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useDebounce } from '@/hooks/useDebounce';

import { SEARCH_FIELD_DEFAULTS } from './constants';
import { SearchFieldPill } from './SearchField.styles';
import { TSearchFieldProps } from './types';

const SearchField = ({
  onSearch,
  value,
  placeholder,
  testId = SEARCH_FIELD_DEFAULTS.testId,
  className,
  isDisabled = false,
  shouldUseDebounce = true,
  onEscape,
}: TSearchFieldProps) => {
  const intl = useIntl();
  const debouncedSearch = useDebounce(onSearch);
  const [inputValue, setInputValue] = useState(value ?? '');

  useEffect(() => {
    // INFO: value is the caller's search term. It only moves on its own when the caller resets it
    // (Question Panel closed, question selected), which has to clear the text still on screen.
    // Typing never arrives here out of order because the debounce always carries the latest value.
    if (value !== undefined) setInputValue(value);
  }, [value]);

  const handleChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);

      if (shouldUseDebounce) {
        debouncedSearch(newValue);
        return;
      }

      onSearch(newValue);
    },
    [shouldUseDebounce, debouncedSearch, onSearch],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // INFO: Escape belongs to the field only while it has something to clear. An empty field lets
      // the event bubble so the surrounding surface can still act on it.
      if (e.key !== 'Escape' || !onEscape || inputValue === '') return;

      e.preventDefault();
      e.stopPropagation();
      setInputValue('');
      onEscape();
    },
    [onEscape, inputValue],
  );

  //INFO: className carries the host layout's own geometry class, such as the table toolbar's search
  //width, so the field never has to know which surface it sits on
  const rootClassName = className ? `form-group ${className}` : 'form-group';

  return (
    <SearchFieldPill className={rootClassName} onKeyDown={handleKeyDown}>
      <div className="input-group">
        <span className="input-group-addon">
          <Icon iconName="icon-search" aria-hidden="true" />
        </span>
        <ClearableInput
          data-testid={testId}
          placeholder={placeholder ?? intl.formatMessage({ id: SEARCH_FIELD_DEFAULTS.placeholderMessageId })}
          value={inputValue}
          onChange={handleChange}
          disabled={isDisabled}
        />
      </div>
    </SearchFieldPill>
  );
};

export default SearchField;
